import {
  DateFilter,
  GenericMultipleFilter,
  Loader,
} from '@energybox/react-ui-library/dist/components';
import {
  Incident,
  IncidentPriority,
  IncidentStatus,
  SingleGenericFilter,
} from '@energybox/react-ui-library/dist/types';
import {
  capitalizeFirstLetterOnly,
  filterResolvedIncidentsOutsideOfTimeRange,
} from '@energybox/react-ui-library/dist/utils';

import React, { useEffect } from 'react';
import IncidentPriorityFilter from '../../../../components/Filters/IncidentPriorityFilter';
import IncidentStatusFilter from '../../../../components/Filters/IncidentStatusFilter';
import SiteFilter from '../../../../components/Filters/SiteFilter/SiteFilter';
import HappyHornMessage from '../../../../components/HappyHornMessage';
import IncidentsCardList from '../../../../components/IncidentsCardList';
import NotificationsTopNavigationBar from '../../../../components/TopNavigationBar/NotificationsTopNavigationBar';
import { useTimeFilter } from '../../../../hooks/useFilters';
import useIncidentPriorityFilter from '../../../../hooks/useIncidentPriorityFilter';
import useIncidentStatusFilter from '../../../../hooks/useIncidentStatusFilter';
import useSiteFilter from '../../../../hooks/useSiteFilter';
import { useSite } from '../../../../hooks/useSites';
import useAppLocale, {
  useIs12HrTimeFormat,
} from '../../../../hooks/useAppLocale';
import styles from './IncidentsListPage.module.css';
import { redirectToIncidentPageEntry } from '../../../../actions/incidents';
import { useDispatch } from 'react-redux';

type Props = {
  siteId?: string;
  getIncidents: Function;
};

export enum IncidentsListPageType {
  SINGLE_SITE = 'SINGLE_SITE',
  MULTI_SITE = 'MULTI_SITE',
}

const pageRef = React.createRef<HTMLDivElement>();

const IncidentsListPage: React.FC<Props> = ({ siteId, getIncidents }) => {
  const pageType = siteId
    ? IncidentsListPageType.SINGLE_SITE
    : IncidentsListPageType.MULTI_SITE;

  const dispatch = useDispatch();

  //Hooks
  const site = useSite(siteId);
  const locale = useAppLocale();
  const { timePeriod, setTimeFilter } = useTimeFilter();
  const { fromDate, toDate } = timePeriod;

  const {
    isIncidentPriorityFilterActive,
    selectedIncidentPriorityFilters,
    setFilter: setIncidentPriorityFilter,
    resetFilter: resetIncidentPriorityFilter,
  } = useIncidentPriorityFilter();

  const {
    isIncidentStatusFilterActive,
    selectedIncidentStatusFilters,
    setFilter: setIncidentStatusFilter,
    resetFilter: resetIncidentStatusFilter,
  } = useIncidentStatusFilter();

  const is12HrTimeFormat = useIs12HrTimeFormat();

  useEffect(() => {
    return () => {
      resetIncidentPriorityFilter();
      resetIncidentStatusFilter();
      dispatch(redirectToIncidentPageEntry({}));
    };
  }, [resetIncidentPriorityFilter, resetIncidentStatusFilter]);

  const { selectedSiteFilters } = useSiteFilter();

  const { isLoading = false, data: incidents = undefined } = getIncidents({
    from: fromDate.toISOString(),
    to: toDate.toISOString(),
    siteIds:
      pageType === IncidentsListPageType.SINGLE_SITE
        ? [siteId]
        : selectedSiteFilters,
  });
  //End Hooks

  //Local functions
  const filterIncidents = (): Incident[] => {
    if (!incidents) return [];
    let filteredIncidents: Incident[] = [...incidents];

    if (isIncidentPriorityFilterActive) {
      filteredIncidents = filteredIncidents.filter((i) => {
        return selectedIncidentPriorityFilters.includes(i.incidentPriority);
      });
    }

    if (isIncidentStatusFilterActive) {
      filteredIncidents = filteredIncidents.filter((i) => {
        let incidentStatus = IncidentStatus.ACTIVE;

        if (i.resolved === true) {
          incidentStatus = IncidentStatus.RESOLVED;
        }

        return selectedIncidentStatusFilters.includes(incidentStatus);
      });
    }

    if (
      selectedIncidentStatusFilters.length === 0 ||
      selectedIncidentStatusFilters.includes(IncidentStatus.RESOLVED)
    ) {
      filteredIncidents = filterResolvedIncidentsOutsideOfTimeRange(
        filteredIncidents,
        fromDate,
        toDate
      );
    }

    return filteredIncidents;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <Loader />
        </div>
      );
    }

    if (!incidents || incidents.length === 0) {
      return <HappyHornMessage />;
    }

    return (
      <IncidentsCardList
        siteId={siteId}
        pageType={pageType}
        incidents={incidentsToDisplay}
        listPageRef={pageRef}
      />
    );
  };
  //End Local functions

  const incidentsToDisplay = filterIncidents();
  return (
    <>
      {pageType === IncidentsListPageType.MULTI_SITE && (
        <NotificationsTopNavigationBar path="/incidents" />
      )}

      <div
        className={`${
          pageType === IncidentsListPageType.SINGLE_SITE
            ? styles.singleSiteRoot
            : styles.multiSiteRoot
        }`}
        ref={pageRef}
      >
        <div className={styles.header}>
          <div className={styles.filterContainer}>
            <div className={styles.priorityAndStatusFilters}>
              <IncidentPriorityFilter />
              <IncidentStatusFilter />
            </div>
            <GenericMultipleFilter
              className={styles.mobilePriorityAndStatusFilter}
              dropdownClassName={styles.mobileFilterDropdown}
              title="Filters"
              filters={
                [
                  {
                    title: 'Priority',
                    setFilter: setIncidentPriorityFilter,
                    items: Object.values(IncidentPriority).filter(
                      //TODO: remove this once Mike O cleans up
                      //functionally duplicate priorities in HIGHEST and CRITICAL
                      (p) => p !== IncidentPriority.HIGHEST
                    ),
                    selectedItems: selectedIncidentPriorityFilters,
                    transformItemName: capitalizeFirstLetterOnly,
                  },
                  {
                    title: 'Status',
                    setFilter: setIncidentStatusFilter,
                    items: Object.values(IncidentStatus),
                    selectedItems: selectedIncidentStatusFilters,
                    transformItemName: capitalizeFirstLetterOnly,
                  },
                  pageType === IncidentsListPageType.MULTI_SITE && {
                    title: 'Site',
                    customFilter: () => <SiteFilter isInDropdown={true} />,
                    selectedItems: selectedSiteFilters,
                  },
                ].filter((item) => item) as SingleGenericFilter[]
              }
              alignItemsRight
            />

            {pageType === IncidentsListPageType.MULTI_SITE && (
              <SiteFilter className={styles.siteFilter} />
            )}
          </div>
          <div>
            <DateFilter
              ianaTimeZoneCode={site?.timeZone}
              useCurrentTime
              value={timePeriod}
              setFilter={setTimeFilter}
              customPickerVariant="datetime"
              locale={locale}
              alignItemsRight={true}
              is12HrTimeFormat={is12HrTimeFormat}
            />
          </div>
        </div>

        {renderContent()}
      </div>
    </>
  );
};

export default IncidentsListPage;
